// An action's opened row: how it closed, its context, why it blocks, what to do next, why
// its owner and its rank, and its dates. Also the first message of a conversation started
// from it. See docs/Y2026-Q4/design-structured-next-actions-and-dates.md 7.2 and
// docs/Y2026-Q4/design-action-dates-dependencies-and-ranking.md.
import { useState } from "react";
import { Link } from "react-router-dom";

import type {
  DateMoveSubject,
  NextAction,
  TrackedDate,
} from "../api/hooks/useProjectAgentStatus";
import { useRunName } from "../api/hooks/useRunNames";
import {
  projectSpecialistPath,
  runLinkPath,
} from "../routes/learningAgentPaths";
import { formatDay } from "../utils/attributeFormat";
import {
  type DateTone,
  hardnessLabel,
  itemFacts,
  optionLines,
  type ProjectItems,
  STATUS_LABEL,
  waitCondition,
  waitItems,
  waitParty,
  workedCheck,
} from "../utils/nextActions";
import { agentDisplayLabel } from "../utils/runParticipants";
import { ActionHistory } from "./ActionHistory";
import { Cited } from "./Cited";
import { DateMoveAnswer } from "./DateMoveAnswer";
import own from "./ActionControls.module.css";
import styles from "./NextActionTable.module.css";
import { WrittenBeforeBranch } from "./AgentSlotBar";

/** A decision's numbered option, as its call to action words it, without the number. */
const optionLine = (callToAction: string, option: number): string =>
  optionLines(callToAction).get(option) ?? "";

// The open contact actions sent as one email with this one, any specialist's.
const emailPartners = (
  items: ProjectItems,
  action: NextAction,
): { agent: string; action: NextAction }[] =>
  action.type !== "contact"
    ? []
    : [...items.actions.values()].filter(
        ({ action: other }) =>
          other.action_id !== action.action_id &&
          !other.closed &&
          (action.send_with.includes(other.action_id) ||
            other.send_with.includes(action.action_id)),
      );

/** Which specialist an item named inline belongs to, after its link. */
export const FromAgent = ({ agent }: { agent: string }) => (
  <span className={styles.small}> (from {agentDisplayLabel(agent)} agent)</span>
);

/** One action's facts, the row and the opened detail read from it. */
export interface Row {
  action: NextAction;
  /** The specialist whose list holds it, and its name. */
  agent: string;
  agentLabel: string;
  /** The version of that specialist's lists the page shows, which a push or resolve quotes. */
  version: number;
  owner: string;
  /** Who closed it, as the opened row names them; "" while open or for an older close. */
  closedBy: string;
  deadline: TrackedDate | undefined;
  tone: DateTone;
  /** Its Building readiness area, ahead of the type line; "" where the table leaves it off. */
  area: string;
}

/** One item another depends on, linked to where its specialist's page shows it, with
 * its date, owner and where it stands. The checklist's Waiting for list shows the same. */
export const DependencyLink = ({
  item,
  items,
  team,
  href,
}: {
  item: string;
  items: ProjectItems;
  team: Record<string, string>;
  href: (agent: string, id: string) => string;
}) => {
  const facts = itemFacts(item, items, team);
  if (!facts)
    return (
      <span className={styles.small}>An item no longer on this project</span>
    );
  return (
    <>
      <Link to={href(facts.agent, facts.anchor)}>{facts.title}</Link>
      <span className={styles.small}>
        {" "}
        {facts.when !== null &&
          `· ${facts.when ? formatDay(facts.when) : "no date"} `}
        {facts.owner && `· ${facts.owner} `}· {facts.state}
      </span>
    </>
  );
};

/** Where an item a wait names stands, as one line of the opened row. */
const WaitLine = ({
  entry,
  items,
  team,
  href,
}: {
  entry: ReturnType<typeof waitItems>[number];
  items: ProjectItems;
  team: Record<string, string>;
  href: (agent: string, id: string) => string;
}) => {
  const facts = itemFacts(entry.item, items, team);
  return (
    <li>
      <DependencyLink item={entry.item} items={items} team={team} href={href} />
      {facts && (
        <>
          <br />
          <span className={styles.small}>
            {waitCondition(entry.needs, facts.owner)}
          </span>
        </>
      )}
    </li>
  );
};

/** The opened row: how it closed, its context, why it blocks, what to do next, why its
 * owner and its rank, and its dates. Also the first message of a conversation started
 * from it. */
export const ActionDetails = ({
  row,
  items,
  agent,
  projectId,
  today,
  team,
  dateMove,
}: {
  row: Row;
  items: ProjectItems;
  agent: string;
  projectId: string;
  today: string;
  team: Record<string, string>;
  /** An agent's open proposal to move this action's due date. */
  dateMove: DateMoveSubject | undefined;
}) => {
  const { action, deadline } = row;
  const closedIn = useRunName(action.closed?.run_id ?? "");
  const [whyOpen, setWhyOpen] = useState(false);
  const closedWhy =
    action.closed?.reason && action.closed.reason !== action.closed.note
      ? action.closed.reason
      : "";
  const href = (agent: string, id: string) =>
    `${projectSpecialistPath(projectId, agent)}#${id}`;
  const waits = waitItems(action);
  const waitStatus = action.status?.status;
  const waiting =
    (waitStatus === "waiting" || waitStatus === "wait_failed") &&
    action.waits_for;
  const waitSide =
    waitParty(action) || (action.waits_for?.on === "us" ? "us" : "them");
  const holdingUp = [...items.actions.values()].filter(
    (entry) =>
      !entry.action.closed &&
      waitItems(entry.action).some((w) => w.item === action.action_id),
  );
  const proposedIn = useRunName(dateMove?.run_id ?? "");
  const moves = action.due_log.filter((m) => m.previous);
  const partners = emailPartners(items, action);
  const resolved = action.resolves
    ? items.checks.get(action.resolves)
    : undefined;
  // The other items the same ask asks for.
  const alsoResolved = (action.also_resolves ?? []).flatMap((id) => {
    const entry = items.checks.get(id);
    return entry ? [entry] : [];
  });
  const deadlinePassed = !!deadline?.date && deadline.date < today;
  const replyDue = action.status?.reply_due ?? "";
  // The document behind a date the record sets: the action's own, else the tracked
  // date's when the action is due on that same date (actions written before 13 Oct 2026).
  const deadlineSource =
    deadline?.source || (deadline?.upload_id ? `[${deadline.upload_id}]` : "");
  const setBy =
    action.due_basis === "target"
      ? ""
      : action.due_source ||
        (!!deadline?.date && deadline.date === action.due
          ? deadlineSource
          : "");
  const dueLabel =
    waitStatus === "reply_received" || waitStatus === "answer_overdue"
      ? "Our answer due"
      : "Due";
  return (
    <div className={styles.sections}>
      {action.blocking && (
        <p className={styles.blocking}>
          <strong>Blocking:</strong> {action.blocking.reason}
        </p>
      )}
      {action.closed && (
        <div className={styles.closedNote}>
          <p>
            <strong>
              {STATUS_LABEL[action.closed.outcome] ?? action.closed.outcome}:
            </strong>{" "}
            {action.closed.note}
          </p>
          {action.closed.chosen_option != null && (
            <p>
              <strong>Chose option {action.closed.chosen_option}:</strong>{" "}
              {optionLine(action.call_to_action, action.closed.chosen_option)}
            </p>
          )}
          <p className={styles.small}>
            {formatDay(action.closed.happened_on || action.closed.at)}
            {row.closedBy && <> by {row.closedBy}</>}
            {action.closed.run_id && (
              <>
                {" "}
                in{" "}
                <Link to={runLinkPath(action.closed.run_id)}>
                  {closedIn || "this run"}
                </Link>
                <WrittenBeforeBranch at={action.closed.at} />
              </>
            )}
            {action.closed.happened_on &&
              action.closed.happened_on !== action.closed.at.slice(0, 10) && (
                <span className={styles.small}>
                  {" "}
                  (recorded {formatDay(action.closed.at)})
                </span>
              )}
            {(closedWhy || action.closed.evidence) && (
              <>
                {" · "}
                <button
                  type="button"
                  className={styles.linkButton}
                  aria-expanded={whyOpen}
                  onClick={() => setWhyOpen((open) => !open)}
                >
                  {whyOpen ? "Less about why" : "More about why"}
                </button>
              </>
            )}
          </p>
          {whyOpen && (
            <div className={styles.closedWhy}>
              {closedWhy && (
                <p className={styles.small}>
                  <strong>Why:</strong> {closedWhy}
                </p>
              )}
              {action.closed.evidence && (
                <div className={styles.small}>
                  <strong>Evidence:</strong>{" "}
                  <Cited text={action.closed.evidence} inline />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <section className={styles.section}>
        <h5>Context</h5>
        {action.context && <Cited text={action.context} />}
        {waiting && (
          <div>
            {/* A wait written before waits named who says only what it waits for:
                naming the email's recipient there would be a guess. */}
            <p>
              <strong>
                {waitStatus === "wait_failed"
                  ? "Needs a look:"
                  : waits.length > 0
                    ? `Waiting on ${waitSide} for ${waits.length} item${waits.length === 1 ? "" : "s"}:`
                    : waitParty(action)
                      ? `Waiting on ${waitParty(action)}:`
                      : "Waiting for:"}
              </strong>{" "}
              {waitStatus === "wait_failed"
                ? "something it waited for closed in a way it does not need; its agent will drop or change it."
                : action.waits_for?.event}
              {action.waits_for?.why && (
                <span className={styles.small}> ({action.waits_for.why})</span>
              )}
            </p>
            {waits.length > 0 && (
              <ul className={own.waitItems}>
                {waits.map((entry) => (
                  <WaitLine
                    key={entry.item}
                    entry={entry}
                    items={items}
                    team={team}
                    href={href}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
        {holdingUp.length > 0 && (
          <p>
            <strong>Holding up:</strong>{" "}
            {holdingUp.map((entry, i) => (
              <span key={entry.action.action_id}>
                {i > 0 && ", "}
                <Link to={href(entry.agent, entry.action.action_id)}>
                  {entry.action.title}
                </Link>
                <FromAgent agent={entry.agent} />
              </span>
            ))}
          </p>
        )}
        {action.contact_log.length > 0 && (
          <details className={styles.history}>
            <summary>Sends and replies</summary>
            {[...action.contact_log].reverse().map((entry) => (
              <div key={`${entry.kind}-${entry.at}`} className={styles.small}>
                {entry.kind === "sent" ? "Sent" : "Reply"} {formatDay(entry.at)}
                {entry.by && ` from ${entry.by}`}:{" "}
                <Cited text={entry.evidence} inline />
              </div>
            ))}
          </details>
        )}
        <ActionHistory
          projectId={projectId}
          agent={agent}
          itemId={action.action_id}
          team={team}
          moves={
            moves.length > 0
              ? { first: action.due_log[0].to, count: moves.length }
              : null
          }
        />
      </section>
      {!action.closed && (
        <section className={styles.section}>
          <h5>What to do next</h5>
          <Cited text={action.call_to_action} />
          {action.sources && (
            <div className={styles.small}>
              <Cited
                text={`Sources:\n${action.sources
                  .split("\n")
                  .map((line) => `- ${line}`)
                  .join("\n")}`}
              />
            </div>
          )}
          {partners.length > 0 && (
            <details className={styles.history}>
              <summary>
                Goes in one email with {partners.length} other ask
                {partners.length === 1 ? "" : "s"}
              </summary>
              {partners.map(({ agent: owner, action: p }) => (
                <div key={p.action_id}>
                  <Link to={href(owner, p.action_id)}>
                    <strong>{p.title}</strong>
                  </Link>
                  <FromAgent agent={owner} />
                  <Cited text={p.call_to_action} />
                </div>
              ))}
            </details>
          )}
        </section>
      )}
      {action.points.length > 0 && (
        <section className={styles.section}>
          <h5>
            What it covers:{" "}
            {action.points.filter((p) => p.state === "open").length} of{" "}
            {action.points.length} open
          </h5>
          {action.points
            .filter((p) => p.state === "open")
            .map((p) => (
              <div key={p.point_id} className={styles.small}>
                <strong>{p.text}</strong> - {p.raised_by},{" "}
                {formatDay(p.raised_on)}: <Cited text={p.raised_in} inline />
              </div>
            ))}
          {action.points.some((p) => p.state !== "open") && (
            <details className={styles.history}>
              <summary>Closed feedback</summary>
              {action.points
                .filter((p) => p.state !== "open")
                .map((p) => (
                  <div key={p.point_id} className={styles.small}>
                    {p.text} - {p.state} {formatDay(p.closed_on)}
                    {p.closed_note && ` (${p.closed_note})`}
                    {p.closed_in && (
                      <>
                        : <Cited text={p.closed_in} inline />
                      </>
                    )}
                  </div>
                ))}
            </details>
          )}
        </section>
      )}
      {!action.closed && !workedCheck(items, action) && (
        <section className={styles.section}>
          <h5>Ready to build</h5>
          <p>
            <span className={`${styles.chip} ${styles.missed}`}>
              No checklist item
            </span>{" "}
            {resolved
              ? resolved.check.closed
                ? "The item it worked towards was closed; the agent links it to another or closes it on its next pass."
                : "The item it worked towards is met; the agent closes it on its next pass."
              : "It works towards no Ready to build item; the agent links it or closes it on its next pass."}
          </p>
        </section>
      )}
      {resolved && (action.closed || workedCheck(items, action)) && (
        <section className={styles.section}>
          <h5>Ready to build</h5>
          <p>
            Resolves{" "}
            <Link to={href(resolved.agent, "ready-to-build")}>
              {resolved.check.text}
            </Link>
            {resolved.check.met && " (now met)"}
            {alsoResolved.map((entry) => (
              <span key={entry.check.check_id}>
                {"; "}
                <Link to={href(entry.agent, "ready-to-build")}>{entry.check.text}</Link>
                {entry.check.met && " (now met)"}
              </span>
            ))}
          </p>
        </section>
      )}
      {!action.closed && action.owner_reason && (
        <section className={styles.section}>
          <h5>Why this owner</h5>
          <p>
            {row.owner}: {action.owner_reason}
            {action.owner_rule && (
              <span className={styles.small}>
                {" "}
                (team rule {action.owner_rule})
              </span>
            )}
          </p>
        </section>
      )}
      {!action.closed && (action.due || replyDue || deadline) && (
        <section className={styles.section}>
          <h5>Dates</h5>
          {(replyDue || action.due || (deadline?.date && !deadline.closed)) && (
            <dl className={styles.dates}>
              {deadline?.date && !deadline.closed && (
                <>
                  <dt>
                    {deadline.hardness === "hard" ? "Deadline" : "Target"}
                  </dt>
                  <dd>
                    {formatDay(deadline.date)} - {deadline.what}{" "}
                    <span className={styles.small}>
                      ({hardnessLabel(deadline.hardness).toLowerCase()}
                      {deadlinePassed && ", passed"})
                    </span>
                  </dd>
                </>
              )}
              {replyDue && (
                <>
                  <dt>Reply due</dt>
                  <dd>
                    {formatDay(replyDue)}
                    {waitStatus === "to_chase"
                      ? ", nothing from them: chase now"
                      : ", we chase that day if nothing has come"}
                    {action.status?.reply_basis && (
                      <span className={styles.small}>
                        {" "}
                        ({action.status.reply_basis})
                      </span>
                    )}
                  </dd>
                </>
              )}
              {action.reply_by && (
                <>
                  <dt>They said</dt>
                  <dd>
                    by {formatDay(action.reply_by)}{" "}
                    {action.reply_by_source && (
                      <span className={styles.small}>
                        <Cited text={`(${action.reply_by_source})`} inline />
                      </span>
                    )}
                    {action.long_wait && (
                      <span className={styles.small}>
                        {" "}
                        Long wait: {action.long_wait}
                      </span>
                    )}
                  </dd>
                </>
              )}
              {/* An email awaiting a reply is due the day the reply is: one line above. */}
              {action.due && !replyDue && (
                <>
                  <dt>{dueLabel}</dt>
                  <dd>
                    {formatDay(action.due)}
                    {action.due_reason && `, ${action.due_reason}`}{" "}
                    <span className={styles.small}>
                      {action.due_basis === "target" ? (
                        "(our own target)"
                      ) : setBy ? (
                        <Cited text={`(set by ${setBy})`} inline />
                      ) : (
                        "(set by the record: a contract, letter or counterparty)"
                      )}
                    </span>
                  </dd>
                </>
              )}
            </dl>
          )}
          {deadline && (
            <details className={styles.history}>
              <summary>What happens if we miss this date</summary>
              <p>
                This works towards the{" "}
                {hardnessLabel(deadline.hardness).toLowerCase()}{" "}
                <strong>
                  {deadline.date ? `${formatDay(deadline.date)} - ` : ""}
                  {deadline.what}
                </strong>
                {deadlinePassed && ", which has passed"}.
              </p>
              {deadline.consequence && (
                <p>If that is missed: {deadline.consequence}</p>
              )}
              {deadlineSource && !setBy && (
                <p className={styles.small}>
                  <Cited
                    text={`That date is set by ${deadlineSource}.`}
                    inline
                  />
                </p>
              )}
            </details>
          )}
          {dateMove && (
            <div className={own.proposal}>
              <p>
                <strong>
                  {agentDisplayLabel(dateMove.agent)} agent proposes a new date
                </strong>
              </p>
              <p>
                <s>{formatDay(dateMove.current)}</s> →{" "}
                {formatDay(dateMove.proposed)}.{" "}
                <Cited text={dateMove.reason} inline />
              </p>
              <DateMoveAnswer
                questionId={dateMove.question_id}
                current={dateMove.current}
                proposed={dateMove.proposed}
                pushAllowed={dateMove.push_allowed}
              />
              <p className={styles.small}>
                Proposed in {proposedIn || "a run"} on{" "}
                {formatDay(dateMove.proposed_at.slice(0, 10))}. The date doesn't
                move until you answer.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
};
